import { useParams } from 'react-router-dom';
import { getServiceBySlug } from '../data/services';
import { usePopup } from '../context/PopupContext';
import { Check, Search, FileText, ShieldCheck, ChevronLeft, ChevronRight } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import RevisedPricingSection from '../components/RevisedPricingSection';
import CTABanner from '../components/CTABanner';
import FAQSection from '../components/FAQSection';
import { useState, useCallback } from 'react';

import trademarkImg from '../assets/images/trademark.png';

export default function ServicePage() {
  const { slug } = useParams();
  const service = getServiceBySlug(slug);
  const { openPopup } = usePopup();
  
  const [benefitIndex, setBenefitIndex] = useState(0);
  const [isAnimatingBenefit, setIsAnimatingBenefit] = useState(false);
  const [benefitDrag, setBenefitDrag] = useState({ isDragging: false, startX: 0, currentX: 0 });

  const totalBenefits = service.benefits?.length || 0;
  const maxBenefitIndex = totalBenefits - 3;

  const benefitNext = useCallback(() => {
    if (isAnimatingBenefit) return;
    setIsAnimatingBenefit(true);
    setBenefitIndex((prev) => (prev >= maxBenefitIndex ? 0 : prev + 1));
    setTimeout(() => setIsAnimatingBenefit(false), 500);
  }, [isAnimatingBenefit, maxBenefitIndex]);

  const benefitPrev = useCallback(() => {
    if (isAnimatingBenefit) return;
    setIsAnimatingBenefit(true);
    setBenefitIndex((prev) => (prev <= 0 ? maxBenefitIndex : prev - 1));
    setTimeout(() => setIsAnimatingBenefit(false), 500);
  }, [isAnimatingBenefit, maxBenefitIndex]);

  const goToBenefitSlide = useCallback((index) => {
    if (isAnimatingBenefit || index > maxBenefitIndex) return;
    setIsAnimatingBenefit(true);
    setBenefitIndex(index);
    setTimeout(() => setIsAnimatingBenefit(false), 500);
  }, [isAnimatingBenefit, maxBenefitIndex]);

  // Drag handlers for benefits slider
  const handleBenefitMouseDown = useCallback((e) => {
    setBenefitDrag({ isDragging: true, startX: e.clientX, currentX: 0 });
  }, []);

  const handleBenefitMouseMove = useCallback((e) => {
    if (!benefitDrag.isDragging) return;
    const diff = e.clientX - benefitDrag.startX;
    setBenefitDrag(prev => ({ ...prev, currentX: diff }));
  }, [benefitDrag.isDragging, benefitDrag.startX]);

  const handleBenefitMouseUp = useCallback(() => {
    if (!benefitDrag.isDragging) return;
    const threshold = 50;
    if (benefitDrag.currentX > threshold) {
      benefitPrev();
    } else if (benefitDrag.currentX < -threshold) {
      benefitNext();
    }
    setBenefitDrag({ isDragging: false, startX: 0, currentX: 0 });
  }, [benefitDrag.isDragging, benefitDrag.currentX, benefitPrev, benefitNext]);

  const handleBenefitMouseLeave = useCallback(() => {
    if (benefitDrag.isDragging) {
      setBenefitDrag({ isDragging: false, startX: 0, currentX: 0 });
    }
  }, [benefitDrag.isDragging]);

  // Touch handlers for mobile
  const handleBenefitTouchStart = useCallback((e) => {
    const touch = e.touches[0];
    setBenefitDrag({ isDragging: true, startX: touch.clientX, currentX: 0 });
  }, []);

  const handleBenefitTouchMove = useCallback((e) => {
    if (!benefitDrag.isDragging) return;
    const touch = e.touches[0];
    const diff = touch.clientX - benefitDrag.startX;
    setBenefitDrag(prev => ({ ...prev, currentX: diff }));
  }, [benefitDrag.isDragging, benefitDrag.startX]);

  const handleBenefitTouchEnd = useCallback(() => {
    if (!benefitDrag.isDragging) return;
    const threshold = 50;
    if (benefitDrag.currentX > threshold) {
      benefitPrev();
    } else if (benefitDrag.currentX < -threshold) {
      benefitNext();
    }
    setBenefitDrag({ isDragging: false, startX: 0, currentX: 0 });
  }, [benefitDrag.isDragging, benefitDrag.currentX, benefitPrev, benefitNext]);

  if (!service) {
    return (
      <main className="min-h-screen flex items-center justify-center pt-20">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white font-['Outfit'] mb-4">Service Not Found</h1>
          <p className="text-[#A0AABF]">The service you are looking for does not exist.</p>
        </div>
      </main>
    );
  }

  const processSteps = [
    { num: "01", icon: Search, title: "Initial Consultation", desc: "We begin with a thorough assessment of your needs, understanding your goals and providing expert guidance on the best path forward." },
    { num: "02", icon: FileText, title: "Preparation & Filing", desc: "Our team prepares all necessary documentation with meticulous attention to detail, ensuring accuracy and compliance at every step." },
    { num: "03", icon: ShieldCheck, title: "Completion & Protection", desc: "We see the process through to completion, delivering results and ensuring your interests are fully protected moving forward." }
  ];

  return (
    <main data-testid="service-page">
      <SEOHead
        title={`${service.title} | Trademark Legal Forum`}
        description={service.heroSubtitle}
        ogImage={service.heroImage}
      />
      {/* Hero Banner */}
      <section data-testid="service-hero" className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={service.heroImage}
            alt={service.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#0A1128]/85" />
        </div>
        <div className="container mx-auto px-6 md:px-12 relative z-10">
          <div className="max-w-3xl">
            <p className="uppercase text-xs tracking-[0.2em] font-bold text-[#D4AF37] mb-4 font-['Outfit']">{service.category}</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white font-['Outfit'] tracking-tight leading-none mb-6">
              {service.title}
            </h1>
            <p className="text-base md:text-lg text-[#A0AABF] leading-relaxed max-w-2xl mb-8">
              {service.heroSubtitle}
            </p>
            <button
              data-testid="service-hero-cta"
              onClick={() => openPopup()}
              className="bg-[#D4AF37] text-[#0A1128] font-bold py-4 px-10 rounded-sm text-sm transition-all duration-300 hover:bg-[#F3E5AB] hover:-translate-y-1 shadow-[0_4px_14px_0_rgba(212,175,55,0.39)] font-['Outfit'] tracking-wider uppercase"
            >
              {service.heroButtonText || "Get Started"}
            </button>
          </div>
        </div>
      </section>

      {/* Info Section with Image */}
      <section data-testid="service-info" className="py-20 md:py-32 bg-[#0D1729]">
        <div className="container mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="uppercase text-xs tracking-[0.2em] font-bold text-[#D4AF37] mb-4 font-['Outfit']">{service.overviewLabel || "Overview"}</p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white font-['Outfit'] tracking-tight mb-6">
                {service.overviewHeading || service.title}
              </h2>
              {service.paragraphs?.map((p, i) => (
                <p key={i} className="text-[#A0AABF] leading-relaxed mb-4">{p}</p>
              ))}
              <button
                data-testid="service-info-cta"
                onClick={() => openPopup()}
                className="mt-4 bg-[#D4AF37] text-[#0A1128] font-bold py-3 px-8 rounded-sm text-sm transition-all duration-300 hover:bg-[#F3E5AB] hover:-translate-y-0.5 shadow-[0_4px_14px_0_rgba(212,175,55,0.39)] font-['Outfit'] tracking-wide"
              >
                {service.overviewButtonText || "Get Started Today"}
              </button>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-[#D4AF37]/5 rounded-sm" />
              <img
                src={service.infoImage}
                alt={service.title}
                className="relative rounded-sm w-full h-auto object-cover shadow-2xl"
              />
              <div className="absolute -bottom-4 -right-4 w-24 h-24 border-2 border-[#D4AF37]/30 rounded-sm" />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      {service.benefits && (
        <section data-testid="service-benefits" className="py-20 md:py-32 bg-[#0A1128]">
          <div className="container mx-auto px-6 md:px-12">
            <div className="text-center mb-16">
              <p className="uppercase text-xs tracking-[0.2em] font-bold text-[#D4AF37] mb-4 font-['Outfit']">{service.benefitsLabel || "Benefits"}</p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white font-['Outfit'] tracking-tight">
                {service.benefitsHeading || "Why Choose This Service"}
              </h2>
            </div>
            
            <div className="relative">
              <style>{`
                .benefits-slider {
                  --translate-percent: -34%;
                }
                @media (max-width: 639px) {
                  .benefits-slider {
                    --translate-percent: -100%;
                  }
                }
                @media (min-width: 640px) and (max-width: 1023px) {
                  .benefits-slider {
                    --translate-percent: -50%;
                  }
                }
              `}</style>
              <div className="overflow-hidden px-2">
                <div 
                  className="flex transition-transform duration-500 ease-in-out gap-6 benefits-slider cursor-grab active:cursor-grabbing"
                  style={{ 
                    '--slide-index': benefitIndex,
                    transform: `translateX(calc(var(--slide-index) * var(--translate-percent)))`
                  }}
                  onMouseDown={handleBenefitMouseDown}
                  onMouseMove={handleBenefitMouseMove}
                  onMouseUp={handleBenefitMouseUp}
                  onMouseLeave={handleBenefitMouseLeave}
                  onTouchStart={handleBenefitTouchStart}
                  onTouchMove={handleBenefitTouchMove}
                  onTouchEnd={handleBenefitTouchEnd}
                >
                  {service.benefits.map((b, i) => (
                    <div key={i} data-testid={`service-benefit-${i}`} className="w-full sm:w-[calc(50%-0.75rem)] lg:w-[calc(33.333%-0.666rem)] flex-shrink-0 bg-[#000000] border border-[#1D2D50] rounded-sm p-8 hover:border-[#D4AF37]/30 hover:-translate-y-1 transition-all duration-300">
                      <div className="w-10 h-10 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mb-4">
                        <Check size={18} className="text-[#D4AF37]" />
                      </div>
                      <h3 className="text-lg font-bold text-white font-['Outfit'] mb-3">{b.title}</h3>
                      <p className="text-[#A0AABF] text-sm leading-relaxed">{b.desc}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Nav arrows */}
              <button
                onClick={benefitPrev}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 w-10 h-10 bg-[#0A1128] border border-[#1D2D50] rounded-full flex items-center justify-center text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors hidden sm:flex"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                onClick={benefitNext}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 w-10 h-10 bg-[#0A1128] border border-[#1D2D50] rounded-full flex items-center justify-center text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors hidden sm:flex"
              >
                <ChevronRight size={18} />
              </button>

              {/* Dots */}
              <div className="flex justify-center gap-2 mt-8">
                {Array.from({ length: maxBenefitIndex + 1 }).map((_, i) => (
                  <button
                    key={i}
                    onClick={() => goToBenefitSlide(i)}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      benefitIndex === i ? 'bg-[#D4AF37] w-6' : 'bg-[#1D2D50]'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Pricing - only for trademark registration */}
      {service.hasPricing && <RevisedPricingSection />}

      {/* FAQ */}
      {service.faqs && (
        <FAQSection
          faqs={service.faqs.map(f => ({ q: f.q, a: f.a }))}
          label={service.faqLabel}
          title={service.faqHeading || `${service.title} - FAQ`}
        />
      )}

      {/* CTA */}
      <CTABanner
        label={service.ctaLabel}
        heading={service.ctaHeading}
        description={service.ctaDescription}
        buttonText={service.ctaButtonText}
      />
    </main>
  );
}
